import { useCallback, useEffect, useMemo, useState } from "react";
import { useVerifyEventPasswordMutation } from "@ac-expo/api";

export interface EventPasswordStorageAdapter {
  getItem: (key: string) => string | null | undefined;
  setItem: (key: string, value: string) => void;
}

export interface UseEventPasswordProtectionArgs {
  eventId?: string;
  eventSlug?: string;
  hasPassword?: boolean;
  storage?: EventPasswordStorageAdapter;
}

export interface UseEventPasswordProtectionResult {
  isAuthenticated: boolean;
  shouldAskPassword: boolean;
  isVerifyingPassword: boolean;
  verifyPassword: (password: string) => Promise<string | void>;
}

export const EVENT_PASSWORD_TOKEN_PREFIX = "event-token-";

export const useEventPasswordProtection = ({
  eventId,
  eventSlug,
  hasPassword,
  storage,
}: UseEventPasswordProtectionArgs): UseEventPasswordProtectionResult => {
  const storageKey = useMemo(() => {
    if (!eventSlug) return null;
    return `${EVENT_PASSWORD_TOKEN_PREFIX}${eventSlug}`;
  }, [eventSlug]);

  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (!hasPassword) {
      return true;
    }

    if (!storageKey || !storage) {
      return false;
    }

    const token = storage.getItem(storageKey);
    return !!token;
  });

  useEffect(() => {
    if (!hasPassword) {
      setIsAuthenticated(true);
      return;
    }

    if (!storageKey || !storage) {
      setIsAuthenticated(false);
      return;
    }

    const token = storage.getItem(storageKey);
    setIsAuthenticated(!!token);
  }, [hasPassword, storage, storageKey]);

  const { mutateAsync, isPending } = useVerifyEventPasswordMutation();

  const verifyPassword = useCallback(
    async (password: string) => {
      if (!eventId) {
        throw new Error("EVENT_ID_MISSING");
      }

      const { token } = await mutateAsync({ eventId, password });

      if (storageKey && storage) {
        storage.setItem(storageKey, token);
      }

      setIsAuthenticated(true);

      return token;
    },
    [eventId, mutateAsync, storage, storageKey]
  );

  return {
    isAuthenticated,
    shouldAskPassword: !!hasPassword && !isAuthenticated,
    isVerifyingPassword: isPending,
    verifyPassword,
  };
};
